import dayjs from 'dayjs';
import {useI18n} from 'vue-i18n';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';

interface JournalStampComposable {
  formatStamp(date: Date | string | undefined): string;
}

/**
 * The date line on a journal card, e.g. "25.04.2024 – 6pm".
 *
 * Two readers, two lines: the date follows the country in the profile and the clock follows the
 * language, so a German reader gets a 24-hour clock and a reader in the States gets 04/25/2024.
 */
export function useJournalStamp(): JournalStampComposable {
  const {locale} = useI18n();
  const patterns = useProfileDateFormat();

  function formatStamp(date: Date | string | undefined): string {
    if (!date) {
      return '';
    }
    const value = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(value.getTime())) {
      return '';
    }
    // The digits-only form: this line is the one the ticket writes out, and a card is glanced at
    // rather than read (IPD-3229).
    const day = dayjs(value).format(patterns.value.numericDate);
    // The clock keeps its own formatting rather than the pattern's: only the am/pm form gets the
    // design's tight lowercase treatment ("6 PM" -> "6pm"), and squashing a locale that has no day
    // period would mangle it, e.g. German "14 Uhr" -> "14uhr".
    const timeParts = new Intl.DateTimeFormat(locale.value, {hour: 'numeric'}).formatToParts(value);
    const dayPeriod = timeParts.find((p) => p.type === 'dayPeriod')?.value;
    const time = dayPeriod
      ? `${timeParts.find((p) => p.type === 'hour')?.value ?? ''}${dayPeriod.toLowerCase()}`
      : timeParts.map((p) => p.value).join('');
    return `${day} – ${time}`;
  }

  return {formatStamp};
}
